import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { PageLayout, Table, Button, DatePicker } from "../../components/ui";
import Loader from '../../components/ui/Loader';
import { useAuth } from '../../components/shared/AuthContext';
import {
  ReportHeader,
  ReportSelect,
  ReportFooter,
  ReportPagination,
  ReportSummaryCard,
} from "../../components/reports";

const CroCollectionsReport = () => {
  const { permissions, roleName, data } = useAuth();
  console.log("User details:", { roleName, branchId: data?.branchId });

  // Determine if user can view all branches or just their own
  const isSuperAdmin = roleName === "superadmin";
  const isAdmin = roleName === "admin";
  const isDataEntryOperator = roleName === "Data Entry Operator";

  // Get user's branchId - only apply branch filtering for non-superadmin users
  const userBranchId = data?.branchId;
  const shouldFilterByBranch = !isSuperAdmin && userBranchId;

  const [fromDate, setFromDate] = useState(""); // From Date filter
  const [toDate, setToDate] = useState(""); // To Date filter
  const today = new Date().toISOString().split("T")[0];
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState("");
  const [fieldOfficers, setFieldOfficers] = useState([]);
  const [selectedFieldOfficer, setSelectedFieldOfficer] = useState("");
  const [officerCustomers, setOfficerCustomers] = useState([]);
  const [branchManagers, setBranchManagers] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [allTransactions, setAllTransactions] = useState([]);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Fetch branches on component mount - filtered by user's branch if not superadmin
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        let url = `${API_BASE_URL}/api/getModelData/branch_details`;

        // If user is not superadmin, add branch filtering
        if (shouldFilterByBranch) {
          url += `?filter={"id":${userBranchId}}`;
          console.log(`Filtering branches by branchId ${userBranchId} for ${roleName}`);
        } else {
          console.log('No branch filtering applied for superadmin');
        }

        const response = await axios.get(url);
        let branchesData = response.data.data || [];

        // Additional client-side filtering as a safety measure
        if (shouldFilterByBranch) {
          branchesData = branchesData.filter(branch =>
            String(branch.id) === String(userBranchId)
          );
        }

        setBranches(branchesData);

        // Auto-select the user's branch if they're not a superadmin
        if (shouldFilterByBranch && branchesData.length > 0) {
          const branchId = String(userBranchId);
          setSelectedBranch(branchId);

          // Also load field officers for this branch immediately
          fetchFieldOfficersForBranch(branchId);
        }
      } catch (error) {
        console.error("Error fetching branches:", error);
      }
    };
    fetchBranches();
  }, [shouldFilterByBranch, userBranchId, roleName]);

  // Extract field officer loading into separate reusable function
  const fetchFieldOfficersForBranch = async (branchId) => {
    if (!branchId) return;

    try {
      let url = `${API_BASE_URL}/api/getModelData/staff_details`;

      // Optimize by filtering at the API level for the selected branch
      url += `?filter={"branchId":${branchId}}`;

      const response = await axios.get(url);
      const allStaff = Array.isArray(response.data.data)
        ? response.data.data
        : [];

      // Filter field officers for the selected branch
      // Include Admin(2), Field Officer(3), Data Entry(4), Manager(5)
      const relevantRoles = [2, 3, 4, 5];
      const filteredFieldOfficers = allStaff.filter(
        (staff) =>
          Number(staff.branchId) === Number(branchId) &&
          relevantRoles.includes(Number(staff.roleId))
      );

      console.log("Filtered Field Officers for branch", branchId, ":", filteredFieldOfficers);
      setFieldOfficers(filteredFieldOfficers);

      // Filter branch managers for the selected branch
      // We no longer need separate filteredManagers list as they are included in fieldOfficers (now "staff")
      setBranchManagers([]);
    } catch (error) {
      console.error("Error fetching staff for branch", branchId, ":", error);
      setFieldOfficers([]);
      setBranchManagers([]);
    }
  };

  const handleBranchChange = async (e) => {
    const branchId = e.target.value;
    setSelectedBranch(branchId);
    setSelectedFieldOfficer("");
    setOfficerCustomers([]);
    setReportData([]);

    // Fetch field officers for the selected branch
    await fetchFieldOfficersForBranch(branchId);
  };

  const handleFieldOfficerChange = async (e) => {
    const officerId = e.target.value;
    setSelectedFieldOfficer(officerId);
    setReportData([]);

    if (!officerId) {
      setOfficerCustomers([]);
      return;
    }

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/getModelData/personal_details`,
        {
          params: {
            filter: JSON.stringify({ fieldOfficerId: Number(officerId) }),
            includeDepth: 1
          }
        }
      );

      const customerData = Array.isArray(response.data.data)
        ? response.data.data
        : [];
      setOfficerCustomers(customerData);
    } catch (error) {
      console.error("Error fetching customers for field officer:", error);
      setOfficerCustomers([]);
    }
  };

  useEffect(() => {
    if (fromDate && toDate) {
      generateReport();
    }
  }, [fromDate, toDate, selectedBranch, selectedFieldOfficer]);

  const generateReport = async () => {
    if (!fromDate || !toDate) {
      return;
    }

    setIsLoading(true);

    try {
      const params = {
        fromDate,
        toDate,
      };

      if (selectedBranch) {
        params.branchId = selectedBranch;
      } else if (shouldFilterByBranch) {
        params.branchId = userBranchId;
      }

      if (selectedFieldOfficer) {
        params.fieldOfficerId = selectedFieldOfficer;
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/getCroCollectionsReportOptimized`,
        { params }
      );

      if (response.data.status === "success") {
        setReportData(response.data.data);
      } else {
        console.error("Error from API:", response.data.message);
        setReportData([]);
      }
    } catch (error) {
      console.error("Error generating report:", error);
      setReportData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0'); // Day
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month
    const year = date.getFullYear(); // Year
    return `${day}/${month}/${year}`; // DD/MM/YYYY format
  };

  // Pagination logic
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = reportData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(reportData.length / rowsPerPage);

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const exportToExcel = () => {
    if (!currentRows || currentRows.length === 0) {
      alert("No data to export");
      return;
    }

    // Prepare worksheet data
    const worksheetData = [
      // Header row
      [
        "S.No",
        "Date",
        "Branch Name",
        "Field Officer Name",
        "Actual Customers",
        "Collected Customers",
      ],

      // Data rows
      ...currentRows.map((row, index) => [
        index + 1,
        formatDate(row.date),
        row.branchName,
        row.fieldOfficerName,
        row.actualCustomers,
        row.collectedCustomers,
      ]),

      // Empty row for spacing
      [],

      // Summary information
      [`Total Customers: ${currentRows.length}`],
      [`Report Period: ${formatDate(fromDate)} to ${formatDate(toDate)}`],
      [`Generated on: ${formatDate(new Date())}`],
    ];

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Style header row as bold
    const range = XLSX.utils.decode_range(worksheet["!ref"]);
    for (let c = range.s.c; c <= range.e.c; c++) {
      const headerCell = XLSX.utils.encode_cell({ r: 0, c });
      if (!worksheet[headerCell]) continue;
      worksheet[headerCell].s = { font: { bold: true } };
    }

    // Set column widths
    const cols = [
      { wch: 8 },     // S.No
      { wch: 12 },    // Date
      { wch: 20 },    // Branch Name
      { wch: 25 },    // Field Officer Name
      { wch: 15 },    // Actual Customers
      { wch: 15 },    // Collected Customers
    ];
    worksheet["!cols"] = cols;

    // Append worksheet to workbook and write file
    XLSX.utils.book_append_sheet(workbook, worksheet, "CRO Collections Report");

    // Generate filename with date range
    const fileName = `CRO_Collection_Report_${formatDate(fromDate)}_to_${formatDate(toDate)}.xlsx`;

    XLSX.writeFile(workbook, fileName);
  };

  const totalActual = reportData.reduce((s, r) => s + (Number(r.actualCustomers) || 0), 0);
  const totalCollected = reportData.reduce((s, r) => s + (Number(r.collectedCustomers) || 0), 0);

  return (
    <PageLayout className="h-auto min-h-[calc(100vh-120px)] overflow-y-auto pb-6">
      {/* Top Header Row: Reusable Report Header with Filters and Export */}
      <ReportHeader
        onExport={exportToExcel}
        exportDisabled={!currentRows || currentRows.length === 0}
        filters={
          <>
            {/* From Date */}
            <div className="w-[145px]">
              <DatePicker
                label="From Date"
                selected={fromDate}
                onChange={(date) =>
                  setFromDate(
                    date
                      ? typeof date === "string"
                        ? date
                        : date.toISOString().split("T")[0]
                      : ""
                  )
                }
                placeholder="DD-MM-YYYY"
              />
            </div>

            {/* To Date */}
            <div className="w-[145px]">
              <DatePicker
                label="To Date"
                selected={toDate}
                onChange={(date) =>
                  setToDate(
                    date
                      ? typeof date === "string"
                        ? date
                        : date.toISOString().split("T")[0]
                      : ""
                  )
                }
                placeholder="DD-MM-YYYY"
              />
            </div>

            {/* Branch Selection */}
            {isSuperAdmin && (
              <ReportSelect
                label="Branch"
                value={selectedBranch}
                onChange={handleBranchChange}
                options={branches}
                placeholder="Select Branch"
                valueKey="id"
                labelKey="branchName"
              />
            )}

            {/* Staff Selection */}
            <ReportSelect
              label="Staff Name"
              value={selectedFieldOfficer}
              onChange={handleFieldOfficerChange}
              options={fieldOfficers}
              placeholder="Select Staff"
              disabled={!selectedBranch && isSuperAdmin}
              valueKey="id"
              labelKey="name"
            />
          </>
        }
      />

      {isLoading && <Loader message="Generating report, please wait..." />}

      <div className="flex-grow flex flex-col min-h-0">
        <Table>
          <Table.Head>
            <tr>
              <Table.HeadCell align="center">S.No</Table.HeadCell>
              <Table.HeadCell align="center">Date</Table.HeadCell>
              <Table.HeadCell align="left">Branch Name</Table.HeadCell>
              <Table.HeadCell align="left">Staff Name</Table.HeadCell>
              <Table.HeadCell align="center">Actual Customers</Table.HeadCell>
              <Table.HeadCell align="center">Collected Customers</Table.HeadCell>
            </tr>
          </Table.Head>
          <Table.Body>
            {currentRows.length > 0 ? (
              currentRows.map((row, index) => (
                <Table.Row key={index}>
                  <Table.Cell align="center">{indexOfFirstRow + index + 1}</Table.Cell>
                  <Table.Cell align="center">{formatDate(row.date)}</Table.Cell>
                  <Table.Cell align="left">{row.branchName}</Table.Cell>
                  <Table.Cell align="left" className="font-semibold text-primary">{row.fieldOfficerName}</Table.Cell>
                  <Table.Cell align="center">{row.actualCustomers}</Table.Cell>
                  <Table.Cell align="center" className="font-medium text-emerald-600">{row.collectedCustomers}</Table.Cell>
                </Table.Row>
              ))
            ) : (
              <Table.EmptyRow
                colSpan={6}
                message={
                  !selectedFieldOfficer && !selectedBranch
                    ? "Please select all filters to view the report."
                    : "No data available for the selected criteria."
                }
              />
            )}
          </Table.Body>
        </Table>

        {/* Bottom Row: Reusable ReportFooter with Pagination & Summary Cards */}
        {reportData.length > 0 && (
          <ReportFooter
            pagination={
              <ReportPagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={reportData.length}
                rowsPerPage={rowsPerPage}
                onPageChange={(p) => setCurrentPage(p)}
                onRowsPerPageChange={handleRowsPerPageChange}
                pageSizeOptions={[5, 10, 20, 50, 100]}
              />
            }
            summary={
              <div className="flex items-center gap-3">
                <ReportSummaryCard
                  label="Actual Customers"
                  value={totalActual}
                  variant="primary"
                  formatCurrency={false}
                />
                <ReportSummaryCard
                  label="Collected Customers"
                  value={totalCollected}
                  variant="emerald"
                  formatCurrency={false}
                />
              </div>
            }
          />
        )}
      </div>
    </PageLayout>
  );
};

export default CroCollectionsReport;